import { useEnum } from "@/api/services/global/enum";
import { TJobListOutputDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { CenteredCell } from "@/components/commons/CenteredCell";
import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { JobTagList } from "@/features/job/components/JobTagList";
import { Tag } from "@/components/elements/Tag";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { findLabelByValue } from "@/utils/object";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { JOB_PAGE_STORAGE_KEY, JOB_SELECTED_STORAGE_KEY, JOB_SELECT_ALL_STORAGE_KEY } from "@/features/job/constants/common";
import { TJobDisplayColumnSetting } from "@/features/job/components/job-list/job-filter-shared";

type TJobListTableProps = {
  rows: TJobListOutputDto[];
  isLoading: boolean;
  displayColumnSettings?: TJobDisplayColumnSetting[] | null;
  onSelectionChange?: (selectedIds: number[], isAllSelected: boolean) => void;
  selectionResetKey?: number;
};

export const JobListTable: FC<TJobListTableProps> = ({
  rows,
  isLoading,
  displayColumnSettings,
  onSelectionChange,
  selectionResetKey,
}) => {
  const navigate = useNavigate();
  const { data: jobStatusOptions } = useEnum("EJobStatus");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");

  const truncateText = (value: string | null | undefined) => {
    if (!value) return EMPTY_STR.TEXT;
    return truncateWithEllipsis(value, 15);
  };

  const columns = useMemo<MRT_ColumnDef<TJobListOutputDto>[]>(
    () => [
      {
        id: "status",
        header: "ステータス",
        accessorKey: "status",
        maxSize: 134,
        Cell: ({ row }) => {
          if (row.original?.company?.isArchive) {
            return (
              <CenteredCell>
                <Tag variant="outline" color={text.secondary} title="アーカイブ済み" />
              </CenteredCell>
            );
          }
          return (
            <CenteredCell>
              <JobStatusTag status={row.original?.status} title={findLabelByValue(jobStatusOptions, row.original.status)} variant="bold" />
            </CenteredCell>
          );
        },
        Header: <TableSorting name="job.status">ステータス</TableSorting>,
      },
      {
        id: "tag",
        header: "タグ",
        Header: <TableSorting name="job.tag">タグ</TableSorting>,
        Cell: ({ row }) => {
          return (
            <CenteredCell>
              <JobTagList tagIds={row.original?.tagIds ?? []} createdAt={row.original?.createdAt} maxVisible={3} />
            </CenteredCell>
          );
        },
        muiTableHeadCellProps: {
          sx: { minWidth: 196, maxWidth: 300 },
        },
        muiTableBodyCellProps: {
          sx: { minWidth: 196, maxWidth: 300 },
        },
      },
      {
        id: "managementNumber",
        header: "求人管理番号",
        Header: <TableSorting name="job.managementNumber">求人管理番号</TableSorting>,
        maxSize: 88,
        Cell: ({ row }) => truncateText(row.original?.managementNumber) ?? EMPTY_STR.TEXT,
      },
      {
        id: "companyName",
        header: "企業名",
        maxSize: 200,
        Cell: ({ row }) => truncateText(row.original?.company?.companyName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="company.companyName">企業名</TableSorting>,
      },
      {
        id: "jobName",
        header: "求人タイトル",
        maxSize: 156,
        Cell: ({ row }) => truncateText(row.original?.jobName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="job.jobName">求人タイトル</TableSorting>,
      },
      {
        id: "recruiterId",
        header: "求人担当者",
        maxSize: 120,
        Cell: ({ row }) => truncateText(row.original?.recruiter?.fullName) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="recruiter.fullName">求人担当者</TableSorting>,
      },
      {
        id: "companySpecificIndustryId",
        header: "分野",
        maxSize: 92,
        Cell: ({ row }) => truncateText(row.original?.companySpecificIndustry?.companySpecificIndustry) ?? EMPTY_STR.TEXT,
        Header: <TableSorting name="companySpecificIndustry.companySpecificIndustry">分野</TableSorting>,
      },
      {
        id: "japaneseLevelType",
        header: "必要な日本語レベル",
        maxSize: 120,
        Cell: ({ row }) =>
          row.original.japaneseLevelType ? truncateText(findLabelByValue(japaneseLevelTypeOptions, row.original.japaneseLevelType)) : EMPTY_STR.TEXT,
        Header: <TableSorting name="job.japaneseLevelType">必要な日本語レベル</TableSorting>,
      },
      {
        id: "recruitmentNumber",
        header: "募集人数",
        maxSize: 86,
        Cell: ({ row }) => (row.original?.recruitmentNumber != null ? `${row.original.recruitmentNumber}名` : EMPTY_STR.TEXT),
      },
      {
        id: "workplace",
        header: "勤務場所",
        maxSize: 131,
        Cell: ({ row }) => truncateText(row.original?.workplace) ?? EMPTY_STR.TEXT,
      },
      {
        id: "recruitmentLocationType",
        header: "募集地",
        maxSize: 84,
        Cell: ({ row }) =>
          row.original.recruitmentLocationType
            ? truncateText(findLabelByValue(recruitmentLocationTypeOptions, row.original.recruitmentLocationType))
            : EMPTY_STR.TEXT,
      },
      {
        id: "createdAt",
        header: "登録日",
        maxSize: 106,
        Cell: ({ row }) => (row.original?.createdAt ? dayjs(row.original.createdAt).format("YYYY/MM/DD") : EMPTY_STR.TEXT),
        Header: <TableSorting name="job.createdAt">登録日</TableSorting>,
      },
    ],
    [jobStatusOptions, recruitmentLocationTypeOptions, japaneseLevelTypeOptions],
  );

  const toDetailRoute = (jobId: number) => {
    const query = new URLSearchParams(location.search);
    const currentPage = query.get("page") || "1";
    sessionStorage.setItem(JOB_PAGE_STORAGE_KEY, currentPage);

    const route = getDynamicRoute(AppRoutes.jobDetail, { jobId: jobId });
    navigate(route);
  };

  const rowsRef = useRef(rows);
  const isSyncingSelection = useRef(false);
  const isInitialMount = useRef(true);

  const userClickedSelectAll = useRef(false);
  const prevIsAllRowsSelected = useRef(false);
  const allRowIdsWhenSelectAll = useRef<number[]>([]);
  const prevSelectedCount = useRef(0);
  const [heightTable] = useState<string>("calc(100vh - 343px)");

  const [isSelectAllMode, setIsSelectAllMode] = useState<boolean>(() => {
    return sessionStorage.getItem(JOB_SELECT_ALL_STORAGE_KEY) === "true";
  });
  const [selectedIds, setSelectedIds] = useState<number[]>(() => {
    const raw = sessionStorage.getItem(JOB_SELECTED_STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  });
  const [forceIndeterminate, setForceIndeterminate] = useState(false);

  const { table } = useTable({
    columns,
    rows,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    pinningColumnsLeft: ["mrt-row-select"],
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    onClickRow: (row) => toDetailRoute(row.id),
    renderEmptyRowsFallback: ({}) => <Box></Box>,
    muiSelectAllCheckboxProps: ({ table }) => ({
      indeterminate: forceIndeterminate && table.getIsAllRowsSelected(),
      checked: isSelectAllMode || (!forceIndeterminate && table.getIsAllRowsSelected()),
      onChange: (event) => {
        if (event.target.checked) {
          setForceIndeterminate(false);
          userClickedSelectAll.current = true;
          allRowIdsWhenSelectAll.current = rows.map((row) => row.id);
          setIsSelectAllMode(true);
          sessionStorage.setItem(JOB_SELECT_ALL_STORAGE_KEY, "true");
          sessionStorage.setItem(JOB_SELECTED_STORAGE_KEY, JSON.stringify([]));
          setSelectedIds([]);
        } else {
          setForceIndeterminate(false);
          userClickedSelectAll.current = false;
          allRowIdsWhenSelectAll.current = [];
          setIsSelectAllMode(false);
          sessionStorage.setItem(JOB_SELECT_ALL_STORAGE_KEY, "false");
          sessionStorage.setItem(JOB_SELECTED_STORAGE_KEY, JSON.stringify([]));
          setSelectedIds([]);
        }
        table.getToggleAllRowsSelectedHandler()(event);
      },
    }),
  });

  const tableSelectedIds = useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel().rows]);

  const currentPageSelectedIds = useMemo(() => {
    const currentRowIds = rows.map((r) => r.id);
    return selectedIds.filter((id: number) => currentRowIds.includes(id));
  }, [rows, selectedIds]);

  useEffect(() => {
    const prevRowIds = rowsRef.current.map((r) => r.id).sort((a, b) => a - b);
    const currentRowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const rowsChanged = prevRowIds.length !== currentRowIds.length || prevRowIds.some((id, idx) => id !== currentRowIds[idx]);

    if (isInitialMount.current || rowsChanged) {
      isInitialMount.current = false;
      rowsRef.current = rows;
      isSyncingSelection.current = true;

      const expectedSelection: MRT_RowSelectionState = {};

      if (isSelectAllMode) {
        const excludedRaw = sessionStorage.getItem(JOB_SELECTED_STORAGE_KEY);
        const excludedIds: number[] = excludedRaw ? JSON.parse(excludedRaw) : [];
        rows.forEach((row) => {
          if (!excludedIds.includes(row.id)) {
            expectedSelection[row.id.toString()] = true;
          }
        });
        userClickedSelectAll.current = true;
      } else {
        currentPageSelectedIds.forEach((id: number) => {
          expectedSelection[id.toString()] = true;
        });
        userClickedSelectAll.current = false;
      }

      table.setRowSelection(expectedSelection);
      allRowIdsWhenSelectAll.current = [];
    }
  }, [rows, currentPageSelectedIds, table, isSelectAllMode]);

  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentIsAllRowsSelected = table.getIsAllRowsSelected();
    const currentSelectedCount = tableSelectedIds.length;

    if (!prevIsAllRowsSelected.current && currentIsAllRowsSelected) {
      if (!userClickedSelectAll.current) {
        setForceIndeterminate(true);
      }
    }

    if (currentSelectedCount === 0) {
      userClickedSelectAll.current = false;
      allRowIdsWhenSelectAll.current = [];
      setForceIndeterminate(false);
    }

    prevIsAllRowsSelected.current = currentIsAllRowsSelected;
    prevSelectedCount.current = currentSelectedCount;
  }, [table.getIsAllRowsSelected(), tableSelectedIds.length, rows]);

  useEffect(() => {
    if (isSyncingSelection.current) return;

    const currentRowIds = rows.map((r) => r.id);
    const newSelectedIds = tableSelectedIds;
    const deselectedIds = currentRowIds.filter((id) => !newSelectedIds.includes(id));

    if (isSelectAllMode) {
      setSelectedIds((prev: number[]) => {
        const otherPageExcluded = prev.filter((id) => !currentRowIds.includes(id));
        const thisPageExcluded = deselectedIds;
        const updated = [...otherPageExcluded, ...thisPageExcluded];

        if (updated.length === prev.length && updated.every((id) => prev.includes(id))) {
          return prev;
        }
        sessionStorage.setItem(JOB_SELECTED_STORAGE_KEY, JSON.stringify(updated));
        return updated;
      });
      return;
    }

    setSelectedIds((prev: number[]) => {
      const filteredPrev = prev.filter((id: number) => !deselectedIds.includes(id));
      const merged = Array.from(new Set([...filteredPrev, ...newSelectedIds]));

      sessionStorage.setItem(JOB_SELECTED_STORAGE_KEY, JSON.stringify(merged));

      return merged;
    });
  }, [tableSelectedIds, rows, isSelectAllMode]);

  useEffect(() => {
    if (isSyncingSelection.current) return;

    if (isSelectAllMode) {
      onSelectionChange?.(selectedIds, true);
    } else if (userClickedSelectAll.current) {
      const uncheckedIds = allRowIdsWhenSelectAll.current.filter((id) => !tableSelectedIds.includes(id));
      onSelectionChange?.(uncheckedIds, true);
    } else {
      onSelectionChange?.(selectedIds, false);
    }
  }, [selectedIds, tableSelectedIds, onSelectionChange, isSelectAllMode]);

  useEffect(() => {
    if (isSyncingSelection.current) {
      isSyncingSelection.current = false;
    }
  });

  const prevResetKeyLocal = useRef(selectionResetKey);
  useEffect(() => {
    if (selectionResetKey === prevResetKeyLocal.current) return;
    prevResetKeyLocal.current = selectionResetKey;

    setIsSelectAllMode(false);
    setSelectedIds([]);
    setForceIndeterminate(false);
    userClickedSelectAll.current = false;
    prevIsAllRowsSelected.current = false;
    allRowIdsWhenSelectAll.current = [];
    prevSelectedCount.current = 0;
    table.setRowSelection({});
  }, [selectionResetKey, table]);

  useEffect(() => {
    if (!displayColumnSettings) return;

    const visibleIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder(["mrt-row-select", ...visibleIds]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  return (
    <Table
      table={table}
      isLoading={isLoading}
      height={heightTable}
      minHeight={150}
      headRowHeight={40}
      bodyRowHeight={42}
      borderColor={divider.high}
    />
  );
};
